// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
// ── Instance Lifecycle ───────────────────────────────────────────
/**
 * Every status a form instance can hold — the two workflow profiles' states
 * together (`WORKFLOW_PROFILES` in `constants/workflow-profiles.ts`).
 *
 * fix-sprint-20260411-0420-followup F6 — the union carried only the seven
 * standalone states while the orchestration service writes `completed` (submit
 * of an embedded record) and `finalized` (its terminal state), so
 * `GET /forms/instances?status=finalized` answered 400 for records that exist
 * and the publishing label switch reached its `never` arm. One array, so the
 * list route's query enum is derived from it rather than restating it.
 * `superseded` is declared by the embedded profile but nothing writes it, so it
 * is deliberately not a member.
 */
export const FORM_INSTANCE_STATUSES = [
    'draft',
    'in_progress',
    'review',
    'approved',
    'generated',
    'exported',
    'archived',
    'completed',
    'finalized',
];
/**
 * Sprint 63.37.0 (F4) — which document of an impact-assessment filing a form
 * is: the `notice` that opens the filing (and names its report), the `report`
 * filed with it, or the `update` that changes a filed dossier. Absent means
 * `notice`, which every profile declared before this field was.
 *
 * Sprint 63.40.0 (B0) — `standalone_notice`: a notice that opens a dossier of
 * its own and belongs to no impact assessment (a service notification). It is
 * a separate document, not a notice with fields left out, so every reader of an
 * assessment notice (`isNoticeSubmissionProfile`, the `assessment_kind`
 * queries, the email pack) passes it by without reading it.
 */
export const FORM_SUBMISSION_DOCUMENTS = [
    'notice',
    'report',
    'update',
    'standalone_notice',
];
/** Every assessment kind, in declared order. */
export const FORM_ASSESSMENT_KINDS = ['dpia', 'otia'];
/**
 * Sprint 63.40.0 (B0, decision 5) — the dossier types a pack may open with a
 * `standalone_notice` profile. A declared vocabulary, so a pack names one of
 * these and never an arbitrary `DossierType` (a standalone notice cannot open a
 * `dpia` dossier), and application code names no instrument to reach it.
 */
export const STANDALONE_NOTICE_DOSSIER_TYPES = ['telecom_service_notification'];
/** Whether a value is one of {@link STANDALONE_NOTICE_DOSSIER_TYPES}. */
export function isStandaloneNoticeDossierType(value) {
    return STANDALONE_NOTICE_DOSSIER_TYPES.includes(value);
}
/** A profile that opens a dossier of its own (Sprint 63.40.0). */
export function isStandaloneNoticeSubmissionProfile(profile) {
    return !!profile && profile.document === 'standalone_notice';
}
/** The document a profile describes; absent is a notice. */
export function submissionProfileDocument(profile) {
    if (!profile)
        return null;
    return profile.document ?? 'notice';
}
/**
 * The assessment kinds a profile is filed under: one for a notice or a report,
 * one or more for an update. Empty for no profile, and for a value that is no
 * kind (the pack validator refuses one).
 */
export function submissionProfileAssessmentKinds(profile) {
    const raw = profile?.assessment_kind;
    const list = Array.isArray(raw) ? raw : [raw];
    return FORM_ASSESSMENT_KINDS.filter((k) => list.includes(k));
}
/** A profile that opens a filing — the only kind a filing capability is resolved from. */
export function isNoticeSubmissionProfile(profile) {
    return !!profile && (profile.document === undefined || profile.document === 'notice');
}
/**
 * Sprint 63.37.0 — the transforms a prefill declaration may name. Read by the
 * engine (`forms/form-prefill-service.ts`) and the pack validator.
 *
 * - `date` — an instant or a date written as `YYYY-MM-DD`.
 * - `datetime` — an instant written as an ISO-8601 string.
 * - `join` — a list written as one line per item.
 * - `count` — a list written as how many items it has.
 * - `range` — a `{min, max}` written as `min–max` (or one number when equal).
 * - `option_map` — a source value mapped to one of the field's option values
 *   through `option_map`; a value the map does not name leaves the field empty.
 * - `today` — the date the form is filled, ignoring the source's value.
 * - `label` — an `enum` source's value written as its label in the fill's
 *   language, from the labels the source registry declares for it
 *   (`FormPrefillSourceDefinition.enum_labels`). For a free-text field that
 *   asks in words for what the platform holds as a code (Sprint 63.37.0).
 *   A `list` source that declares `enum_values` is written one label per line
 *   (legal second read S12); a member with no label leaves the field for a
 *   person rather than printing the code.
 * - `country_name` — a `country` source's ISO 3166-1 alpha-2 code written as
 *   the country's name in the fill's language (`countryName`, the runtime's
 *   own region data). A value that names no country leaves the field for a
 *   person (legal second read S12).
 *
 * A source that holds codes (`enum`, `country`, or a `list` declaring
 * `enum_values`) is never written into a free-text field as stored: the pack
 * validator refuses that declaration (`PREFILL_CODE_AS_ANSWER`).
 */
export const FORM_PREFILL_TRANSFORMS = [
    'date',
    'datetime',
    'join',
    'count',
    'range',
    'option_map',
    'today',
    'label',
    'country_name',
];
// ── "The content is final" — one vocabulary, two profiles ────────
//
// fix-sprint-20260826-2129 F4. A form instance reaches a state where its
// content is documentary evidence by two different routes, and the two use
// DIFFERENT words. A standalone form goes `approved` → `generated` →
// `exported`; an embedded regulatory record goes `completed` → `finalized`.
// `form-output-service.ts` has always known this and kept two allowlists side
// by side. `handbook-evidence-candidates.ts` hand-typed a third — mixing one
// word from each profile (`completed`, `finalized`, `approved`) — and so
// accepted a state a standalone form can never hold while rejecting the two it
// actually ends in. Generating a form's official output flipped `approved` to
// `generated` and the handbook checklist then reclassified the very artefact
// that made it evidence as `unsupported`.
//
// The sets live here, once, so a fourth caller cannot invent a fourth
// vocabulary. `generated` and `exported` appear in both because output
// generation is shared by both profiles.
/** Standalone (`standalone_form_workflow`) states whose content is final. */
export const STANDALONE_FORM_COMPLETE_STATUSES = [
    'approved',
    'generated',
    'exported',
];
/** Embedded (`embedded_regulatory_record`) states whose content is final. */
export const EMBEDDED_FORM_COMPLETE_STATUSES = [
    'completed',
    'finalized',
    'generated',
    'exported',
];
/**
 * Whether a form instance's content is final, for the profile it belongs to.
 *
 * An instance is embedded exactly when it carries a `parent_entity_type` —
 * the same test `form-output-service.ts` applies before choosing an allowlist.
 */
export function formContentIsFinal(status, parentEntityType) {
    const allowed = parentEntityType
        ? EMBEDDED_FORM_COMPLETE_STATUSES
        : STANDALONE_FORM_COMPLETE_STATUSES;
    return status !== undefined && allowed.includes(status);
}
/**
 * Sprint 62.57.0 Slice E (work file D64) — dossier types retired, not deleted.
 *
 * Retired in 62.57.0 Slice E — certificate applications are records of the
 * service-certificate register; stored dossiers of these types are read, never
 * created. `POST /forms/dossiers` refuses them (422 `DOSSIER_TYPE_RETIRED`), the
 * sample-package validator refuses them, and the list filter still accepts them
 * so a stored dossier stays findable. The cover letter keeps their cases for
 * stored dossiers.
 */
export const RETIRED_DOSSIER_TYPES = [
    'processing_service_certificate',
    'certificate_change',
    'certificate_reissue',
    'certificate_revocation',
];
/** Whether a value is one of {@link RETIRED_DOSSIER_TYPES}: read, never created. */
export function isRetiredDossierType(value) {
    return RETIRED_DOSSIER_TYPES.includes(value);
}
/**
 * Fix sprint 20261026-2106 (Finding 2) — the statuses in which a notice
 * dossier's applicant statements and addressee may still be answered or named:
 * before it is filed, and when the receiving body has asked for a supplement
 * (the dossier goes back to `submitted` from there). Once filed, the answers are
 * the record of what was filed, and the server refuses a change (409
 * `DOSSIER_NOT_EDITABLE`).
 */
export const NOTICE_DOSSIER_EDITABLE_STATUSES = [
    'draft',
    'needs_supplement',
];
/**
 * Fix sprint 20261026-1110 (item 1) — the field of a standalone notice form
 * whose answer opens its dossier's `addressee_name`, as the pack declares it,
 * or `null`. Only a profile naming a class of body has one.
 */
export function declaredAddresseeFieldId(profile) {
    if (!isStandaloneNoticeSubmissionProfile(profile))
        return null;
    if (profile.receiving_authority_is_class !== true)
        return null;
    const id = profile.addressee_field_id;
    return typeof id === 'string' && id.trim() ? id.trim() : null;
}
/** Whether a dossier in `status` still accepts statement answers and an addressee. */
export function isNoticeDossierEditable(status) {
    return NOTICE_DOSSIER_EDITABLE_STATUSES.includes(status);
}
/**
 * Sentinel used in the `authority` field of response-kind capability
 * entries. Tells the materialisation service to pull authority from the
 * source entity (e.g., `AuthorityRequest.authority_name`) at runtime rather
 * than from the registry entry.
 */
export const AUTHORITY_FROM_REQUEST_SENTINEL = '__from_request__';
/**
 * Sprint 63.33.0 (#398) — sentinel in the `authority` field of a filing
 * capability whose receiving authority the installed FRAMEWORK RECORD names.
 *
 * Nine filing rows spelled their authority as a platform constant (`'MIC'`),
 * an authority name in application code (R-46) and, for the Data Law rows, the
 * wrong body: the framework record names the Ministry of Public Security. The
 * materialisation service now reads the authority from the organisation's
 * installed record of the capability's framework (`frameworkReceivingAuthority`)
 * and refuses, rather than inventing one, when the record names none.
 */
export const AUTHORITY_FROM_FRAMEWORK_SENTINEL = '__from_framework__';
/**
 * The body a filing under this framework is addressed to, as the installed
 * framework record declares it: its `lead_authority` — the administering body,
 * as distinct from `authority_name`, the body that ISSUED the instrument (a
 * decree's issuer is the Government; the filings it prescribes go to a
 * ministry). `null` when the record declares none; a caller never falls back to
 * the issuer, because that is a different fact.
 *
 * Reads the English value of a locale map, matching the `authority` string a
 * dossier carries.
 */
export function frameworkReceivingAuthority(record) {
    const value = record?.lead_authority;
    if (typeof value === 'string')
        return value.trim() || null;
    if (value && typeof value === 'object') {
        const en = value.en;
        return typeof en === 'string' && en.trim() ? en.trim() : null;
    }
    return null;
}
/**
 * Sprint 63.33.0 (AC13, `legal` second reading) — the applicants a filing
 * row's route serves, when one form is filed with different bodies depending
 * on who files it.
 *
 * The first case: one notification form, filed by a foreign organisation
 * providing services across the border with the ministry the framework record
 * names, and by an enterprise established in the framework's own jurisdiction
 * with a different body, on a different instrument's form. The platform
 * prepares the first and not the second, so the row says which applicant it
 * serves and the materialisation service refuses every other applicant with the
 * route the installed record states, instead of addressing a dossier to a body
 * that does not receive it.
 */
/**
 * Sprint 63.40.0 (decision 6) — the two sides of a jurisdiction an applicant
 * route serves. `established_in_framework_jurisdiction` serves only an
 * organisation whose recorded `country` equals the installed record's
 * `jurisdiction_code`; `established_outside_framework_jurisdiction` only one
 * whose country differs. Both refuse an organisation with no readable country.
 */
export const SUBMISSION_APPLICANT_ROUTE_SERVES = [
    'established_outside_framework_jurisdiction',
    'established_in_framework_jurisdiction',
];
/**
 * fix-sprint-20260905-2135-followup-2 residue (run 10), R-46 — sentinel used in
 * the `authority` field of a static filing-capability entry whose receiving
 * authority the platform must not spell. Tells the materialisation service to
 * read it from the organisation's installed forms of the same framework —
 * `submission_profile.receiving_authority`, the field the pack declares the
 * receiving body in, or a form's own `receiving_authority` where it has no
 * profile — and to refuse when those forms name none, or more than one.
 */
export const AUTHORITY_FROM_INSTALLED_FORMS_SENTINEL = '__from_installed_forms__';
// ── Regulatory classification helper ─────────────────────────────
/**
 * Sprint 63.37.0, D3 (idea-catalog #615) — the one rule for an official form.
 *
 * **A form is official exactly when it declares `is_official: true`.** Nothing
 * else counts: not an `official_form_id`, not a `template_source`, not a
 * per-organisation label. The platform has internal forms and official forms
 * (owner ruling OQ-1, 2026-10-17), and this is the line between them.
 *
 * It replaces `isOfficialRegulatoryForm`, which **guessed** when the flag was
 * absent — official whenever an `official_form_id` was present and the source
 * was not an internal mapping — and the frontend's private copy of that guess
 * (`TemplatePicker.isOfficialTemplate`). The guess counted the ten
 * `portal_artifact` records official because each carries a portal or annex
 * number. The guess could go because every pack form now declares the flag
 * (D1) and `validate:packs` refuses a form that does not (D2,
 * `IS_OFFICIAL_MISSING`, `IS_OFFICIAL_CLASSIFICATION_DISAGREE`).
 *
 * A database query states the same rule as the term `is_official: true` —
 * never `{ $ne: false }`, which is the guess in query form. The census
 * `official-form-predicate-census-63.37.0.test.ts` fails on any other read of a
 * form's `is_official`.
 *
 * `GuidanceRecord.is_official` is a different field on a different record type
 * (official guidance text versus commentary) and is not decided here.
 */
export function isOfficialForm(form) {
    return form.is_official === true;
}
/**
 * Sprint 63.37.0 — whether a filing may be opened on this form: an official
 * form with a form number, not an internal mapping, not hidden, current
 * (neither legacy, draft nor upcoming), and not a document the authority
 * issues (`authority_generated`), which an organisation receives and never
 * files. The cross-border filing page offers exactly these, and the server
 * refuses any other on create, so the two cannot disagree.
 */
export function isFileableFilingForm(form) {
    return (typeof form.official_form_id === 'string' &&
        form.official_form_id.trim().length > 0 &&
        isOfficialForm(form) &&
        form.authority_generated !== true &&
        form.template_source !== 'internal_mapping' &&
        form.visibility !== 'hidden' &&
        form.lifecycle_status !== 'legacy' &&
        form.lifecycle_status !== 'draft' &&
        form.lifecycle_status !== 'upcoming');
}
/**
 * Returns true when a form should appear in a normal user-facing picker.
 * Portfolio rationalization (2026-04-21): hides forms flagged internal_only/hidden
 * or lifecycle_status=legacy/draft/upcoming. Pass `includeHidden` to override for
 * admin surfaces. `upcoming` distinguishes "promulgated but not yet effective"
 * from `draft` (not-yet-passed).
 *
 * `upcoming` is a claim about a date, and a date passes. A form left `upcoming`
 * after its framework's `effective_date` is silently suppressed from every
 * user-facing picker — which is how all 11 `VN_CYBERSEC_LAW_2025` forms came to
 * be hidden for five weeks after Law 116/2025/QH15 took effect on 2026-07-01
 * (Sprint 62.4.0, Gate E12.5 F2). The example is deliberately not restated here:
 * `validate:packs` now derives both directions from the framework record
 * (FORM_LIFECYCLE_STALE_UPCOMING / FORM_LIFECYCLE_ACTIVE_UNDER_RETIRED_FRAMEWORK)
 * rather than relying on a hand-maintained list anyone must remember to revisit.
 */
export function isUserFacingTemplate(form, opts = {}) {
    if (opts.includeHidden)
        return true;
    const visibility = form.visibility ?? 'normal';
    const lifecycle = form.lifecycle_status ?? 'active';
    if (visibility === 'hidden' || visibility === 'internal_only')
        return false;
    if (lifecycle === 'legacy' || lifecycle === 'draft' || lifecycle === 'upcoming')
        return false;
    return true;
}
/**
 * Sprint 62.0.3 (AC7) — *why* an artefact is provisional.
 *
 * The three-tier classification above was already the right vocabulary; what was
 * missing was the reason. Without it, a provisional DORA register of information
 * — for which implementing technical standards prescribe a template the pack does
 * not hold — is indistinguishable from a provisional internal checklist, and the
 * user cannot tell that an authoritative template exists elsewhere that they
 * should be using instead.
 */
export const PROVISIONAL_ARTEFACT_REASONS = [
    /** An authority- or standards-body-prescribed template exists and is not held. */
    'authoritative_template_exists_not_held',
    /** A source was identified but has not been verified to 62.0.2's citation bar. */
    'awaiting_source_verification',
    /** The operative rule is a national transposition that is not installed. */
    'transposition_unavailable',
    /** No official template exists; this is an internal artefact standing in deliberately. */
    'internal_substitute_no_official_template',
    /**
     * Sprint 62.0.5 — a template has been published and adopted by the competent
     * body, but is **not yet binding**, so an artefact following it is not the
     * prescribed form and must not be labelled official.
     *
     * Two live examples verified 2026-08-01: the EDPB's common data breach
     * notification template is adopted but in public consultation until
     * 5 August 2026 and is explicitly a voluntary harmonisation tool for DPAs
     * rather than a requirement on controllers; and the NIS Cooperation Group's
     * common incident-reporting templates are adopted as *common guidance*, with
     * the Commission intending a later implementing act to make them mandatory.
     *
     * Distinct from `authoritative_template_exists_not_held`, which says we could
     * hold it and do not. Here holding it would still not make the artefact
     * official, because nothing yet prescribes it.
     */
    'template_adopted_not_yet_binding',
    /**
     * Sprint 62.0.5 — **our own template, because the standard's is licensed.**
     *
     * ISO/IEC 27001 and the AICPA Trust Services Criteria are copyrighted works
     * sold under licence. Copyright protects their *expression*, not the
     * underlying requirement, so the correct response is not to ship a thin
     * artefact and cite the licence — it is to author a complete, operationally
     * correct template of our own that discharges the same clause, and to be
     * clear that it is ours rather than the standard's.
     *
     * So an artefact carrying this reason must still be **fully usable**. The
     * licence explains *whose* template it is; it is never an excuse for an
     * incomplete one. A user working to Clause 9.3 gets a management-review
     * template that satisfies Clause 9.3 — it simply is not ISO's document, and
     * control text is referenced by identifier rather than transcribed.
     *
     * Distinct from `internal_substitute_no_official_template`, where no official
     * document exists at all. Here one does, and we deliberately do not ship it.
     */
    'own_template_for_licensed_standard',
];
//# sourceMappingURL=forms.js.map